// Bottom panel tabs for Battlefield (phone + tablet)
// Phase 4: Replaces hidden side rail with swappable tabs — one panel visible at a time
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import Panel from '@/components/ui/Panel';
import TaskList from '@/features/tasks/components/lists/TaskList';
import { AddTaskRow } from '@/components/ui/AddTaskRow';
import UnscheduleDropZone from '@/features/planning/components/shared/UnscheduleDropZone';
import { shouldShowNextActionPanel } from '@/features/tasks/utils/views/nextActionUtils';
import { useT } from '@/i18n/useT';
import {
  listContainsTaskId,
  resolveOnboardingHighlightPanelId,
} from '@/features/planning/components/shared/planOnboardingHighlight';

const PANEL_FLAT_CLASS = 'panel-flat-list';

const railListProps = {
  view: 'plan',
  menuSurface: 'plan-rail',
  showBreadcrumbs: true,
  dragEnabled: true,
  disableStructuralNav: true,
};

const unscheduledListProps = {
  view: 'plan',
  menuSurface: 'plan-rail',
  indentationType: 'full',
  showBreadcrumbs: false,
  addSubtaskOnParentsOnly: true,
  dragEnabled: true,
};

export default function PlanPagePanelTabs({
  overdueTasks,
  nextActions,
  unscheduledTasks,
  overdueRailSlice,
  nextActionRailSlice,
  selectedTaskIds,
  railSelectionHandlers,
  focusOwnerId,
  plannedParentIds,
  waitingForParentIds,
  tasksMap = new Map(),
  onboardingHighlightTaskId = null,
  onboardingHighlightAddTask = false,
  onOnboardingAddTaskUsed,
}) {
  const t = useT();
  const showNextAction = shouldShowNextActionPanel(tasksMap);

  // Unscheduled always visible (drop target for grid → unschedule), like PlanPageRail.
  const panelConfigs = useMemo(
    () => [
      {
        id: 'overdue',
        label: t('plan.overdue', 'Reschedule'),
        count: overdueTasks?.length || 0,
        tasks: overdueRailSlice,
        handler: railSelectionHandlers.overdue,
        isEmpty: !overdueTasks || overdueTasks.length === 0,
      },
      {
        id: 'next-action',
        label: t('plan.nextAction', 'Next Actions'),
        count: nextActions?.length || 0,
        tasks: nextActionRailSlice,
        handler: railSelectionHandlers['next-action'],
        isEmpty:
          !showNextAction || !nextActions || nextActions.length === 0,
      },
      {
        id: 'unscheduled',
        label: t('plan.unscheduled', 'To schedule'),
        count: unscheduledTasks?.length || 0,
        tasks: unscheduledTasks || [],
        handler: railSelectionHandlers.unscheduled,
        isEmpty: false,
      },
    ],
    [
      overdueTasks,
      nextActions,
      unscheduledTasks,
      overdueRailSlice,
      nextActionRailSlice,
      showNextAction,
      t,
      railSelectionHandlers,
    ],
  );

  const visiblePanels = useMemo(
    () => panelConfigs.filter((p) => !p.isEmpty),
    [panelConfigs],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const autoSwitchedHighlightRef = useRef(null);

  useEffect(() => {
    if (!onboardingHighlightTaskId) {
      autoSwitchedHighlightRef.current = null;
      return;
    }
    if (autoSwitchedHighlightRef.current === onboardingHighlightTaskId) {
      return;
    }

    const panelId = resolveOnboardingHighlightPanelId(
      onboardingHighlightTaskId,
      { nextActionRailSlice, unscheduledTasks },
    );
    if (!panelId) {
      return;
    }

    const panelIndex = visiblePanels.findIndex((panel) => panel.id === panelId);
    if (panelIndex >= 0) {
      setSelectedIndex(panelIndex);
      autoSwitchedHighlightRef.current = onboardingHighlightTaskId;
    }
  }, [
    onboardingHighlightTaskId,
    nextActionRailSlice,
    unscheduledTasks,
    visiblePanels,
  ]);

  const safeIndex = selectedIndex < visiblePanels.length ? selectedIndex : 0;
  const activePanel = visiblePanels[safeIndex];

  const activePanelHighlightId =
    onboardingHighlightTaskId &&
    listContainsTaskId(activePanel.tasks, onboardingHighlightTaskId)
      ? onboardingHighlightTaskId
      : null;

  return (
    <UnscheduleDropZone
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 1.5,
        flexShrink: 0,
        maxHeight: '42%',
        minHeight: 160,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={safeIndex}
        onChange={(_, newVal) => setSelectedIndex(newVal)}
        variant="fullWidth"
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {visiblePanels.map((panel) => (
          <Tab
            key={panel.id}
            label={
              panel.count > 0 ? `${panel.label} (${panel.count})` : panel.label
            }
            data-testid={`panel-tab-${panel.id}`}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
        <Panel
          flat
          hideHeader
          contentClassName={PANEL_FLAT_CLASS}
          testId={`panel-${activePanel.id}`}
          title={activePanel.label}
          count={activePanel.count}
        >
          <TaskList
            id={activePanel.id}
            tasks={activePanel.tasks}
            selectedTaskIds={selectedTaskIds}
            onSelectionChange={activePanel.handler}
            listId={activePanel.id}
            focusOwnerId={focusOwnerId}
            plannedParentIds={plannedParentIds}
            waitingForParentIds={waitingForParentIds}
            onboardingHighlightTaskId={activePanelHighlightId}
            {...(activePanel.id === 'unscheduled'
              ? unscheduledListProps
              : railListProps)}
          />
          {activePanel.id === 'unscheduled' ? (
            <AddTaskRow
              tasks={unscheduledTasks || []}
              tabIndex={-1}
              highlight={onboardingHighlightAddTask}
              onOpen={onOnboardingAddTaskUsed}
            />
          ) : null}
        </Panel>
      </Box>
    </UnscheduleDropZone>
  );
}
